import memoize from 'memoize-one';
import {AggregationType, ChartFieldToQueryOption, ChartIntervalUnit, ChartOutput} from 'entities/chart';
import {CSSProperties} from 'react';
import i18n from 'i18n';
import {getSafeConfig} from '@/utils/safeConfig';

export enum ChartType {
    BarChart = 'bar',
    LineChart = 'line',
    AreaChart = 'area',
    PieChart = 'pie',
    SunburstChart = 'sunburst',
    TreemapChart = 'treemap',
    Table = 'table',
    PivotTable = 'pivot',
    Wordcloud = 'wordcloud',
    Concordance = 'concordance',
    TextSamples = 'textsamples',
    TrendingWords = 'trendingwords',
    BigNumber = 'bignumber',
    Comment = 'comment',
}

export enum EchartsChartType {
    BarChart = ChartType.BarChart,
    LineChart = ChartType.LineChart,
    AreaChart = ChartType.AreaChart,
    PieChart = ChartType.PieChart,
    SunburstChart = ChartType.SunburstChart,
    TreemapChart = ChartType.TreemapChart,
}

export enum ChartFilterType {
    NumericalDescriptor = 'numerical_descriptor',
    FeedbackNumericalDescriptor = 'feedback_numerical_descriptor',
    ComputedClassification = 'computed_classification',
    SelectionField = 'selection_field',
    TextSample = 'text_sample',
    FeedbackTextSample = 'feedback_text_sample',
    SummaryTextSample = 'summary_text_sample',
}

const chartTypeNames = [
    [ChartType.BarChart, i18n.t('bar_chart.text')],
    [ChartType.LineChart, i18n.t('line_chart.text')],
    [ChartType.AreaChart, i18n.t('area_chart.text')],
    [ChartType.PieChart, i18n.t('pie_chart.text')],
    [ChartType.SunburstChart, i18n.t('sunburst_chart.text')],
    [ChartType.TreemapChart, i18n.t('treemap_chart.text')],
    [ChartType.Table, i18n.t('table.text')],
    [ChartType.PivotTable, i18n.t('pivot_chart.text')],
    [ChartType.Wordcloud, i18n.t('word_cloud.text')],
    [ChartType.Concordance, i18n.t('concordance.text')],
    [ChartType.TextSamples, i18n.t('text_samples.text')],
    [ChartType.TrendingWords, i18n.t('trending_words.text')],
    [ChartType.BigNumber, i18n.t('big_number_chart.text')],
];
const deprecatedChartTypes = [
    ChartType.Concordance,
    ChartType.TrendingWords,
];
const selectableChartTypeNames = chartTypeNames.filter(
    ([chartType]) => !deprecatedChartTypes.includes(chartType as ChartType),
);
const allChartTypes = chartTypeNames.map((el) => el[0]);
allChartTypes.push(ChartType.Comment); // Allowed value but not a selectable option

export const csvDownloadableTypes = [
    ChartType.Table,
    ChartType.PivotTable,
];

export const chartDownloadableTypes = [
    ChartType.BarChart,
    ChartType.LineChart,
    ChartType.AreaChart,
    ChartType.PieChart,
    ChartType.SunburstChart,
    ChartType.TreemapChart,
];

export const hierarchicalAggregationChartTypes = [
    ChartType.SunburstChart,
    ChartType.TreemapChart,
];

const chartWidths: readonly [number, string][] = [
    [1, '1/4'],
    [2, '1/2'],
    [3, '3/4'],
    [4, i18n.t('full_width.text')],
];
const chartWidthValues: number[] = Array(chartWidths[chartWidths.length - 1][0]).fill(chartWidths[0][0]).map((x, y) => x + y);

const chartHeights: readonly [number, string][] = [
    [1, i18n.t('tiny.text')],
    [2, i18n.t('m_size.text')],
    [3, i18n.t('l_size.text')],
    [4, i18n.t('xl_size.text')],
    [5, i18n.t('huge_size.text')],
];
const chartHeightValues: number[] = Array(chartHeights[chartHeights.length - 1][0]).fill(chartHeights[0][0]).map((x, y) => x + y);

// Make sure that chartTimelineIntervals are ordered in ascending order because this order is used
// to determine period aggregation changes on the go (when too many datapoints error is shown)
const chartTimelineIntervals: [ChartIntervalUnit, string][] = [
    [ChartIntervalUnit.Hour, i18n.t('ch_hour.text')],
    [ChartIntervalUnit.Day, i18n.t('ch_day.text')],
    [ChartIntervalUnit.Week, i18n.t('ch_week.text')],
    [ChartIntervalUnit.Month, i18n.t('ch_month.text')],
    [ChartIntervalUnit.Quarter, i18n.t('ch_quarter.text')],
    [ChartIntervalUnit.Year, i18n.t('ch_year.text')],
];

// Add 'd' here to use days as fixed interval units too, BUT this will cause problems on Elastic side because
// ES and pandas have a different idea of period generation
const fixedIntervalUnits = [
    'h',
];

const defaultIntervalUnit = ChartIntervalUnit.Day;

const chartOutputTypeChoices: Partial<Record<ChartType, ChartOutput[]>> = {
    [ChartType.BarChart]: [
        ChartOutput.TimelineByDate,
        ChartOutput.Terms,
        ChartOutput.TermsTimeline,
        ChartOutput.MultiTermsTimeline,
        ChartOutput.MetricTimeline,
        ChartOutput.TermsMetric,
    ],
    [ChartType.LineChart]: [
        ChartOutput.TimelineByDate,
        ChartOutput.TermsTimeline,
        ChartOutput.MultiTermsTimeline,
        ChartOutput.MetricTimeline,
    ],
    [ChartType.AreaChart]: [
        ChartOutput.TimelineByDate,
        ChartOutput.TermsTimeline,
        ChartOutput.MultiTermsTimeline,
        ChartOutput.MetricTimeline,
    ],
    [ChartType.PieChart]: [ChartOutput.TimelineByDate, ChartOutput.Terms, ChartOutput.MultiTerms],
    [ChartType.SunburstChart]: [ChartOutput.Terms, ChartOutput.MultiTerms],
    [ChartType.TreemapChart]: [ChartOutput.Terms, ChartOutput.MultiTerms],
    [ChartType.Table]: [
        ChartOutput.TimelineByDate,
        ChartOutput.Terms,
        ChartOutput.MultiTerms,
        ChartOutput.TermsTimeline,
        ChartOutput.MultiTermsTimeline,
        ChartOutput.MetricTimeline,
        ChartOutput.TermsMetric,
    ],
    [ChartType.PivotTable]: [ChartOutput.PivotTerms],
    [ChartType.TextSamples]: [],
    [ChartType.TrendingWords]: [ChartOutput.Trending, ChartOutput.Disappearing],
    [ChartType.Concordance]: [],
    [ChartType.BigNumber]: [ChartOutput.TimelineByDate, ChartOutput.Terms, ChartOutput.TermsMetric],
    [ChartType.Wordcloud]: [],
};

const scalableOutputTypes = [
    ChartOutput.MetricTimeline,
    ChartOutput.TermsMetric,
    ChartOutput.Terms,
];

const numericMetricTimelineTypes: [AggregationType, string][] = [
    [AggregationType.Average, i18n.t('ch_average.text')],
    [AggregationType.Sum, i18n.t('ch_sum.text')],
    [AggregationType.Min, i18n.t('ch_min.text')],
    [AggregationType.Max, i18n.t('ch_max.text')],
    // [AggregationType.MedianAbsoluteDeviation, 'Median absolute deviation'],
    [AggregationType.ValueCount, i18n.t('ch_value_count.text')],
];

const chartMetricTimelineTypes: [AggregationType, string][] = [
    ...numericMetricTimelineTypes,
    [AggregationType.Histogram, i18n.t('ch_histogram.text')],
];

const filterNumericalTypes = [
    ChartFilterType.NumericalDescriptor,
    ChartFilterType.FeedbackNumericalDescriptor,
];

const filterSelectionTypes = [
    ChartFilterType.ComputedClassification,
    ChartFilterType.SelectionField,
    ChartFilterType.FeedbackTextSample,
];


const filterFreeTextTypes = [
    ChartFilterType.TextSample,
    ChartFilterType.SummaryTextSample,
];

export type EventFilter = [string, string, ChartFilterType, string, boolean, string, string, string];
export type EventFilterGroup = {
    core: EventFilter[];
    non_prioritised: EventFilter[];
    prioritised: EventFilter[];
    others: EventFilter[];
};

type AggregationChoicesFilters = Record<ChartOutput | 'undefined' | 'default', (choices: EventFilter[]) => EventFilter[]>;
const aggregationChoicesFilters: AggregationChoicesFilters = {
    [ChartOutput.TimelineByDate]: () => [],
    [ChartOutput.TermsTimeline]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.MultiTermsTimeline]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.MetricTimeline]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.TermsMetric]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.Terms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.MultiTerms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.NestedTerms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.Trending]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.Disappearing]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    [ChartOutput.PivotTerms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterSelectionTypes.includes(el[2]))),
    undefined: () => [],
    default: () => [],
};

// Cross table specific aggregation choices
type AggregationChoicesFiltersCross = Record<ChartOutput | 'undefined' | 'default', (choices: EventFilter[]) => EventFilter[]>;
// @ts-ignore
const aggregationChoicesFiltersCross: AggregationChoicesFiltersCross = {
    [ChartOutput.TimelineByDate]: () => [],
    [ChartOutput.TermsTimeline]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.MultiTermsTimeline]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.MetricTimeline]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.TermsMetric]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.Terms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.MultiTerms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.NestedTerms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.Trending]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.Disappearing]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    [ChartOutput.PivotTerms]: memoize((choices: EventFilter[]) => choices.filter((el) => filterNumericalTypes.includes(el[2]))),
    undefined: () => [],
    default: () => [],
};

export interface AggregationChoice {
    type: ChartFilterType;
    label: string;
    value: string;
    description?: string | null;
    label_clean?: string;
    use_case?: string | null;
    scale?: string | null;
    event_type?: string | null;
    isDisabled?: boolean;
}

const chartColors = [
    '#3366cc',
    '#dc3912',
    '#ff9900',
    '#109618',
    '#990099',
    '#0099c6',
    '#dd4477',
    '#66aa00',
    '#b82e2e',
    '#316395',
    '#994499',
    '#22aa99',
    '#aaaa11',
    '#6633cc',
    '#e67300',
    '#8b0707',
    '#651067',
    '#329262',
    '#5574a6',
    '#3b3eac',
    '#b77322',
    '#16d620',
    '#b91383',
    '#f4359e',
    '#9c5935',
    '#a9c413',
    '#2a778d',
    '#668d1c',
    '#bea413',
    '#0c5922',
    '#743411',
];

const chartEditorColors = [
    'text-info',
    'text-primary',
    'text-secondary',
    'text-danger',
    'text-warning',
    'text-success',
    'text-muted',
];

const customizeFormOutputTypeChoices: [ChartOutput, string][] = [
    [ChartOutput.TimelineByDate, i18n.t('ch_timeline_by_date.text')],
    [ChartOutput.Terms, i18n.t('ch_aggregations.text')],
    [ChartOutput.MultiTerms, i18n.t('ch_multi_aggregations.text')],
    [ChartOutput.TermsTimeline, i18n.t('ch_terms_timeline.text')],
    [ChartOutput.MultiTermsTimeline, i18n.t('ch_multi_terms_timeline.text')],
    [ChartOutput.MetricTimeline, i18n.t('ch_metric_timeline.text')],
    [ChartOutput.TermsMetric, i18n.t('ch_metric_aggregations.text')],
    [ChartOutput.Trending, i18n.t('ch_trending.text')],
    [ChartOutput.Disappearing, i18n.t('ch_disappearing.text')],
    [ChartOutput.PivotTerms, i18n.t('pivot_terms.text')],
];

const defaultFieldToQuery = ChartFieldToQueryOption.EventText;

const chartOutputTypeChoicesWithLabels: Partial<Record<
    keyof typeof chartOutputTypeChoices,
    [ChartOutput, typeof customizeFormOutputTypeChoices[number][1]][]
>> = {};
(Object.keys(chartOutputTypeChoices) as (keyof typeof chartOutputTypeChoices)[]).forEach((x) => {
    chartOutputTypeChoicesWithLabels[x] = chartOutputTypeChoices[x]!.map((choice: ChartOutput) => ([
        choice, customizeFormOutputTypeChoices.find((c) => c[0] === choice)![1],
    ]));
});

const maxChartLegendLabelsShown = getSafeConfig().maxChartLegendLabelsShown;

const pivotValueTypes: [string, string][] = [
    ['count', i18n.t('ch_count.text')],
    ...numericMetricTimelineTypes,
];

const chartFontSizeNumber = 13;
const chartFontSize = {fontSize: chartFontSizeNumber};

const tooltipStyles: CSSProperties = {
    ...chartFontSize,
    padding: 5,
    textAlign: 'left',
    lineHeight: '10px',
};

const chartContainerResizeDebounceTime = 5;


export {
    chartTypeNames,
    selectableChartTypeNames,
    deprecatedChartTypes,
    allChartTypes,
    chartWidths,
    chartWidthValues,
    chartHeights,
    chartHeightValues,
    chartTimelineIntervals,
    customizeFormOutputTypeChoices,
    fixedIntervalUnits,
    defaultIntervalUnit,
    chartOutputTypeChoices,
    numericMetricTimelineTypes,
    pivotValueTypes,
    chartMetricTimelineTypes,
    chartOutputTypeChoicesWithLabels,
    chartColors,
    chartEditorColors,
    aggregationChoicesFilters,
    aggregationChoicesFiltersCross, // Cross table aggregation choices
    tooltipStyles,
    chartContainerResizeDebounceTime,
    chartFontSize,
    filterNumericalTypes,
    filterFreeTextTypes,
    filterSelectionTypes,
    maxChartLegendLabelsShown,
    scalableOutputTypes,
    defaultFieldToQuery,
};
